import { ChangeDetectionStrategy, Component, computed, effect, input, output, ViewChild } from '@angular/core';
import { customThemeQuartz } from '@app/ag-grid-custom-theme';
import { AgGridAngular } from 'ag-grid-angular';
import {
    CellValueChangedEvent,
    ColDef,
    GridApi,
    GridOptions,
    SizeColumnsToFitGridStrategy,
    themeQuartz
} from 'ag-grid-enterprise';

@Component({
    selector: 'app-client-side-grid',
    templateUrl: './client-side-grid.component.html',
    styleUrls: ['./client-side-grid.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [AgGridAngular]
})
export class ClientSideGridComponent {
    @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

    readonly columnTypes = {
        textWithTooltip: {
            tooltipValueGetter: (params: { value?: string }) => {
                const value = params.value || '';
                return value.length > 50 || value.includes(' ') ? value : '';
            }
        }
    };

    rowData = input.required<any[]>();
    columnDefs = input.required<ColDef[]>();
    defaultColDef = input<ColDef>({
        resizable: false,
        sortable: false,
        filter: false,
        cellStyle: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        },
        cellClassRules: {
            'editable-cell': (params) => !!params.colDef.editable
        }
    });
    rowHeight = input<number>(40);
    headerHeight = input<number>(40);
    height = input<string>('200px');
    domLayout = input<'normal' | 'autoHeight'>('normal');
    suppressHorizontalScroll = input<boolean>(false);
    enableCellTextSelection = input<boolean>(false);
    autoSizeStrategy = input<SizeColumnsToFitGridStrategy | undefined>(undefined);
    themeConfig = input<any>(customThemeQuartz);

    cellValueChanged = output<CellValueChangedEvent>();
    gridReady = output<GridApi>();

    theme = computed(() => themeQuartz.withParams(this.themeConfig()));

    gridOptions = computed<GridOptions>(() => ({
        columnDefs: this.columnDefs(),
        columnTypes: this.columnTypes,
        defaultColDef: { ...this.defaultColDef(), suppressHeaderMenuButton: true },
        enableCellTextSelection: this.enableCellTextSelection(),
        suppressClipboardPaste: false,
        suppressContextMenu: true,
        stopEditingWhenCellsLoseFocus: true,
        cellSelection: true,
        processCellFromClipboard: (params: { value: string }) => params.value?.trim?.() ?? params.value,
        onCellValueChanged: (event: CellValueChangedEvent) => this.cellValueChanged.emit(event),
        rowHeight: this.rowHeight(),
        headerHeight: this.headerHeight(),
        domLayout: this.domLayout(),
        suppressHorizontalScroll: this.suppressHorizontalScroll(),
        ...(this.autoSizeStrategy() != null && { autoSizeStrategy: this.autoSizeStrategy() }),
        onGridReady: (params) => {
            this.gridApi = params.api;
            this.gridReady.emit(params.api);
        }
    }));

    private gridApi?: GridApi;

    private readonly columnDefsEffect = effect(() => {
        this.columnDefs();
        if (this.gridApi) {
            this.gridApi.setGridOption('columnDefs', this.columnDefs());
            this.gridApi.refreshCells({ force: true });
        }
    });

    getGridApi(): GridApi | undefined {
        return this.gridApi;
    }

    refreshCells(options?: { rowNodes?: any[]; force?: boolean }): void {
        if (this.gridApi) {
            this.gridApi.refreshCells(options || { force: true });
        }
    }

    forEachNode(callback: (node: any) => void): void {
        if (this.gridApi) {
            this.gridApi.forEachNode(callback);
        }
    }

    sizeColumnsToFit(options?: { defaultMinWidth?: number }): void {
        this.gridApi?.sizeColumnsToFit(options ?? { defaultMinWidth: 100 });
    }
}
